import { Icon } from '@/shared/assets/icons';
import { chartColor, chartColorPinned } from '@/shared/lib/chart-theme';
import { cn } from '@/shared/lib/cn';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type Plugin,
  type TooltipItem,
} from 'chart.js';
import { useMemo, type FC } from 'react';
import { Scatter } from 'react-chartjs-2';
import { TooltipElement } from '../tooltip';
import { Typography } from '../typography';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export interface FacilityDataPoint {
  facilityId?: number;
  facilityName: string;
  occupancy: number;
  los: number;
}

interface OccupancyLosScatterChartProps {
  data: FacilityDataPoint[];
  className?: string;
  chartClassName?: string;
  occupancyThreshold?: number;
  losThreshold?: number;
  onPointClick?: (facilityName: string, facilityId?: number) => void;
  highlightFacilityName?: string;
}

enum Quadrant {
  HighOccupancyHighLos = 'High Occupancy & High LOS',
  HighOccupancyLowLos = 'High Occupancy & Low LOS',
  LowOccupancyHighLos = 'Low Occupancy & High LOS',
  LowOccupancyLowLos = 'Low Occupancy & Low LOS',
}

// A function, not a constant: `chartColor` reads the live CSS variables, so
// evaluating this at module scope would freeze it to whichever theme happened to
// be active at import time.
const quadrantColors = () => ({
  [Quadrant.HighOccupancyHighLos]: chartColor('--color-polar-green-3'),
  [Quadrant.HighOccupancyLowLos]: chartColor('--color-calendula-gold-3'),
  [Quadrant.LowOccupancyHighLos]: chartColor('--color-calendula-gold-3'),
  [Quadrant.LowOccupancyLowLos]: chartColor('--color-dust-red-3'),
});

const quadrantPointColors = () => ({
  [Quadrant.HighOccupancyHighLos]: chartColor('--color-polar-green-8'),
  [Quadrant.HighOccupancyLowLos]: chartColor('--color-calendula-gold-9'),
  [Quadrant.LowOccupancyHighLos]: chartColor('--color-calendula-gold-9'),
  [Quadrant.LowOccupancyLowLos]: chartColor('--color-dust-red-7'),
});

const determineQuadrant = (
  occupancy: number,
  los: number,
  occupancyThreshold: number,
  losThreshold: number
): Quadrant => {
  if (occupancy >= occupancyThreshold && los >= losThreshold) {
    return Quadrant.HighOccupancyHighLos;
  } else if (occupancy >= occupancyThreshold && los < losThreshold) {
    return Quadrant.HighOccupancyLowLos;
  } else if (occupancy < occupancyThreshold && los >= losThreshold) {
    return Quadrant.LowOccupancyHighLos;
  } else {
    return Quadrant.LowOccupancyLowLos;
  }
};

interface ThresholdLinesOptions {
  occupancyThreshold: number;
  losThreshold: number;
}

const circularQuadrantsPlugin: Plugin<'scatter'> = {
  id: 'circularQuadrants',
  beforeDatasetsDraw(chart) {
    const {
      ctx,
      scales: { x, y },
      chartArea,
    } = chart;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pluginOptions = (chart.options.plugins as any)?.thresholdLines as
      | ThresholdLinesOptions
      | undefined;
    const occupancyThreshold = pluginOptions?.occupancyThreshold ?? 80;
    const losThreshold = pluginOptions?.losThreshold ?? 270;

    if (!chartArea) return;
    const centerX = x.getPixelForValue(occupancyThreshold);
    const centerY = y.getPixelForValue(losThreshold);

    const width = chartArea.right - chartArea.left;
    const height = chartArea.bottom - chartArea.top;
    const radius = Math.min(width, height) * 0.15;

    ctx.save();

    // Draw quadrants as circle sectors
    ctx.fillStyle = quadrantColors()[Quadrant.HighOccupancyHighLos];
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, -Math.PI / 2, 0);
    ctx.closePath();
    ctx.fill();

    // Bottom Right
    ctx.fillStyle = quadrantColors()[Quadrant.HighOccupancyLowLos];
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, 0, Math.PI / 2);
    ctx.closePath();
    ctx.fill();

    // Bottom Left
    ctx.fillStyle = quadrantColors()[Quadrant.LowOccupancyLowLos];
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, Math.PI / 2, Math.PI);
    ctx.closePath();
    ctx.fill();

    // Top Left
    ctx.fillStyle = quadrantColors()[Quadrant.LowOccupancyHighLos];
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, Math.PI, (3 * Math.PI) / 2);
    ctx.closePath();
    ctx.fill();

    // Draw crosshair lines through the entire chart area
    ctx.strokeStyle = chartColorPinned('--color-neutral-7', '#999999');
    ctx.lineWidth = 1;
    ctx.setLineDash([]);

    // Vertical line through center of chart
    ctx.beginPath();
    ctx.moveTo(centerX, chartArea.top);
    ctx.lineTo(centerX, chartArea.bottom);
    ctx.stroke();

    // Horizontal line through center of chart
    ctx.beginPath();
    ctx.moveTo(chartArea.left, centerY);
    ctx.lineTo(chartArea.right - 90, centerY);
    ctx.stroke();

    ctx.restore();
  },
};

const grayColor = () => chartColorPinned('--color-neutral-7', '#9ca3af');

export const OccupancyLosScatterChart: FC<OccupancyLosScatterChartProps> = ({
  data,
  className,
  chartClassName,
  occupancyThreshold = 80,
  losThreshold = 270,
  onPointClick,
  highlightFacilityName,
}) => {
  const { chartData, quadrantCounts, scaleConfig } = useMemo(() => {
    const counts = {
      [Quadrant.HighOccupancyHighLos]: 0,
      [Quadrant.HighOccupancyLowLos]: 0,
      [Quadrant.LowOccupancyHighLos]: 0,
      [Quadrant.LowOccupancyLowLos]: 0,
    };

    const groupedByQuadrant: Record<
      Quadrant,
      Array<{
        x: number;
        y: number;
        facilityName: string;
        isHighlighted?: boolean;
      }>
    > = {
      [Quadrant.HighOccupancyHighLos]: [],
      [Quadrant.HighOccupancyLowLos]: [],
      [Quadrant.LowOccupancyHighLos]: [],
      [Quadrant.LowOccupancyLowLos]: [],
    };

    // Keep original fixed scale for visual design
    const xMin = occupancyThreshold - 50;
    const xMax = occupancyThreshold + 50;
    const yMin = losThreshold - 200;
    const yMax = losThreshold + 200;

    // Define safety margins from edges (in chart units)
    const edgeSafetyMargin = 8;

    // Get data ranges for smart positioning
    const occupancyValues = data.map((d) => d.occupancy);
    const losValues = data.map((d) => d.los);
    const minOccupancy = Math.min(...occupancyValues);
    const maxOccupancy = Math.max(...occupancyValues);
    const maxLos = Math.max(...losValues);

    // Smart compression function for outliers with edge safety
    const compressValue = (
      value: number,
      min: number,
      max: number,
      threshold: number,
      isOccupancy: boolean
    ): number => {
      // Define safe boundaries (keep dots away from absolute edges)
      const safeMin = min + edgeSafetyMargin;
      const safeMax = max - edgeSafetyMargin;

      // If within safe range, no compression needed
      if (value >= safeMin && value <= safeMax) return value;

      if (value < safeMin) {
        // Below safe minimum - compress into lower margin
        const distanceFromThreshold = threshold - value;
        const maxDistance = threshold - minOccupancy;
        const availableSpace = threshold - safeMin - 5;
        const compressionRatio = Math.min(1, availableSpace / maxDistance);
        return Math.max(
          safeMin,
          threshold - distanceFromThreshold * compressionRatio
        );
      } else {
        // Above safe maximum - compress into upper margin with better distribution
        const distanceFromThreshold = value - threshold;
        const maxDistance = (isOccupancy ? maxOccupancy : maxLos) - threshold;

        if (isOccupancy) {
          // Occupancy compression - linear with cap
          const availableSpace = safeMax - threshold - 5;
          const compressionRatio = Math.min(1, availableSpace / maxDistance);
          return Math.min(
            safeMax,
            threshold + distanceFromThreshold * compressionRatio
          );
        } else {
          // LOS compression - use logarithmic for extreme values
          const availableSpace = safeMax - threshold - 10;
          if (maxDistance <= availableSpace) {
            return Math.min(safeMax, value); // No compression needed but cap at safe max
          }
          // Logarithmic compression for better distribution
          const normalizedPos = distanceFromThreshold / maxDistance;
          const compressedPos = Math.log(normalizedPos * 9 + 1) / Math.log(10);
          return Math.min(safeMax, threshold + compressedPos * availableSpace);
        }
      }
    };

    data.forEach((point) => {
      const quadrant = determineQuadrant(
        point.occupancy,
        point.los,
        occupancyThreshold,
        losThreshold
      );
      counts[quadrant]++;

      const compressedX = compressValue(
        point.occupancy,
        xMin,
        xMax,
        occupancyThreshold,
        true
      );
      const compressedY = compressValue(
        point.los,
        yMin,
        yMax,
        losThreshold,
        false
      );

      const isHighlighted = highlightFacilityName
        ? point.facilityName === highlightFacilityName
        : undefined;

      groupedByQuadrant[quadrant].push({
        x: compressedX,
        y: compressedY,
        facilityName: point.facilityName,
        isHighlighted,
      });
    });

    const datasets = Object.entries(groupedByQuadrant)
      .map(([quadrant, points]) => {
        if (highlightFacilityName) {
          const ownPoints = points.filter((p) => p.isHighlighted);
          const otherPoints = points.filter((p) => !p.isHighlighted);
          return [
            {
              label: `${quadrant}-others`,
              data: otherPoints,
              backgroundColor: grayColor(),
              borderColor: grayColor(),
              pointRadius: 3,
              pointHoverRadius: 3,
            },
            {
              label: quadrant,
              data: ownPoints,
              backgroundColor: quadrantPointColors()[quadrant as Quadrant],
              borderColor: quadrantPointColors()[quadrant as Quadrant],
              pointRadius: 8,
              pointHoverRadius: 10,
            },
          ];
        }
        return [
          {
            label: quadrant,
            data: points,
            backgroundColor: quadrantPointColors()[quadrant as Quadrant],
            borderColor: quadrantPointColors()[quadrant as Quadrant],
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ];
      })
      .flat();

    return {
      chartData: { datasets },
      quadrantCounts: counts,
      scaleConfig: {
        xMin,
        xMax,
        yMin,
        yMax,
      },
    };
  }, [data, occupancyThreshold, losThreshold]);

  const options: ChartOptions<'scatter'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 10,
        right: 10,
        bottom: 10,
        left: 10,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: 'nearest',
        displayColors: false,
        backgroundColor: chartColor('--color-neutral-1'),
        borderColor: chartColorPinned('--color-neutral-5', '#00000030'),
        borderWidth: 1,
        titleColor: chartColor('--color-primary-black'),
        titleFont: {
          size: 14,
        },
        bodyColor: chartColorPinned('--color-neutral-8', '#00000078'),
        bodyFont: {
          size: 13,
        },
        padding: {
          top: 10,
          bottom: 10,
          left: 8,
          right: 8,
        },
        filter: (item: TooltipItem<'scatter'>) => {
          if (!highlightFacilityName) return true;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const point = item.raw as any;
          return point.isHighlighted === true;
        },
        callbacks: {
          title: (items: TooltipItem<'scatter'>[]) => {
            // Empty when `filter` above hides the hovered point — in Single
            // Mode, hovering any facility other than the highlighted one.
            const item = items[0];
            if (!item) return '';
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (item.raw as any).facilityName || '';
          },
          label: (item: TooltipItem<'scatter'>) => {
            const point = item.raw as { x: number; y: number };
            return [
              `LOS: ${point.y.toFixed(0)} days`,
              `Occupancy: ${point.x.toFixed(1)}%`,
            ];
          },
        },
      },
      datalabels: {
        display: false,
      },
      thresholdLines: {
        occupancyThreshold,
        losThreshold,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: false,
        },
        min: scaleConfig.xMin,
        max: scaleConfig.xMax,
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
      },
      y: {
        type: 'linear',
        position: 'left',
        title: {
          display: false,
        },
        min: scaleConfig.yMin,
        max: scaleConfig.yMax,
        reverse: false,
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
      },
    },
  };

  return (
    <div className={cn('relative flex flex-col', className)}>
      {/* Title */}
      <div className="mb-2 text-center">
        <div className="flex items-center justify-center gap-2">
          <Typography variant="h5" weight="medium">
            Occupancy
          </Typography>
          <TooltipElement content="Displays facility performance based on occupancy levels and the median length of stay. LOS is calculated for all residents active during the selected period, measured from their move-in date to either their move-out date or the end of the filtered period" />
        </div>
        <div className="flex items-center justify-center gap-2">
          <Typography className="text-neutral-7" variant="footnote-description">
            ← Less
          </Typography>
          <Typography variant="h5" weight="medium">
            {occupancyThreshold}%
          </Typography>
          <Typography className="text-neutral-7" variant="footnote-description">
            More →
          </Typography>
        </div>
      </div>

      {/* Chart with positioned legends */}
      <div className={cn('relative py-8', chartClassName)}>
        {/* Top Left Label */}
        <div className="absolute -top-4 left-0 flex items-center gap-2">
          <div className="text-calendula-gold-8 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Icon name="smile-neutral" size={20} />
              <Typography variant="h4" weight="medium">
                {quadrantCounts[Quadrant.LowOccupancyHighLos]}
              </Typography>
            </div>
            <Typography variant="footnote-description">
              Low Occupancy & High LOS
            </Typography>
          </div>
        </div>

        {/* Top Right Label */}
        <div className="absolute -top-4 right-0 flex items-center gap-2">
          <div className="text-polar-green-8 flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <Typography variant="h4" weight="medium">
                {quadrantCounts[Quadrant.HighOccupancyHighLos]}
              </Typography>
              <Icon name="smile-happy" size={20} />
            </div>
            <Typography variant="footnote-description">
              High Occupancy & High LOS
            </Typography>
          </div>
        </div>

        {/* Bottom Left Label */}
        <div className="absolute -bottom-4 left-0 flex items-center gap-2">
          <div className="text-dust-red-8 flex flex-col gap-2">
            <Typography variant="footnote-description">
              Low Occupancy & Low LOS
            </Typography>
            <div className="flex items-center gap-2">
              <Icon name="smile-sad" size={20} />
              <Typography variant="h4" weight="medium">
                {quadrantCounts[Quadrant.LowOccupancyLowLos]}
              </Typography>
            </div>
          </div>
        </div>

        {/* Bottom Right Label */}
        <div className="absolute right-0 -bottom-4 flex items-center gap-2">
          <div className="text-calendula-gold-8 flex flex-col items-end gap-2">
            <Typography variant="footnote-description">
              High Occupancy & Low LOS
            </Typography>
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold">
                {quadrantCounts[Quadrant.HighOccupancyLowLos]}
              </span>
              <Icon name="smile-neutral" size={20} />
            </div>
          </div>
        </div>

        {/* Right Side LOS Label */}
        <div className="absolute top-[48.5%] -right-4 flex -translate-y-[50%] flex-col items-end text-right">
          <div className="flex flex-col items-center">
            <Typography
              className="text-neutral-7"
              variant="footnote-description"
            >
              ↑
            </Typography>
            <Typography
              className="text-neutral-7"
              variant="footnote-description"
            >
              More
            </Typography>
          </div>
          <div className="my-2">
            <Typography variant="h5" weight="medium">
              LOS {losThreshold} days
            </Typography>
          </div>
          <div className="flex flex-col items-center">
            <Typography
              className="text-neutral-7"
              variant="footnote-description"
            >
              Less
            </Typography>
            <Typography
              className="text-neutral-7"
              variant="footnote-description"
            >
              ↓
            </Typography>
          </div>
        </div>

        {/* Chart */}
        <div className="relative z-10 h-full">
          <Scatter
            options={options}
            plugins={[circularQuadrantsPlugin]}
            data={chartData as ChartData<'scatter'>}
            style={onPointClick ? { cursor: 'pointer' } : undefined}
            onClick={(event) => {
              if (!onPointClick) return;
              const chart = ChartJS.getChart(
                event.currentTarget as HTMLCanvasElement
              );
              if (!chart) return;
              const elements = chart.getElementsAtEventForMode(
                event.nativeEvent,
                'nearest',
                { intersect: true },
                false
              );
              if (elements.length > 0) {
                const { datasetIndex, index } = elements[0];
                const point = chart.data.datasets[datasetIndex]?.data[
                  index
                ] as {
                  facilityName?: string;
                  facilityId?: number;
                  isHighlighted?: boolean;
                };
                if (highlightFacilityName && !point?.isHighlighted) return;
                if (point?.facilityName) {
                  onPointClick(point.facilityName, point.facilityId);
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};
